import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Badge } from 'react-bootstrap';
import { Table } from '@rd-web-markets/shared/dist/util';
import { EditButton, DeleteButton, SaveButton, CancelButton } from '@rd-web-markets/shared/dist/util/buttons';
const CustomerContractsTable = _ref => {
  let {
    contracts = [],
    onUpdate,
    onDelete,
    userCanEdit,
    loading
  } = _ref;
  const {
    t
  } = useTranslation();
  const UPSTREAM_CONTRACT_OPTIONS = [{
    value: 'received',
    label: t('received')
  }, {
    value: 'review_in_progress',
    label: t('review_in_progress')
  }, {
    value: 'reviewed',
    label: t('reviewed')
  }];
  const UPSTREAM_REVIEW_OPTIONS = [{
    value: 'nq',
    label: t('nq')
  }, {
    value: 'inconclusive',
    label: t('inconclusive')
  }, {
    value: 'q',
    label: t('q')
  }];
  const [editingContractId, setEditingContractId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const getUpstreamContractLabel = value => {
    const option = UPSTREAM_CONTRACT_OPTIONS.find(opt => opt.value === value);
    return option ? option.label : value;
  };
  const getUpstreamReviewLabel = value => {
    const option = UPSTREAM_REVIEW_OPTIONS.find(opt => opt.value === value);
    return option ? option.label : value;
  };
  const handleEdit = (index, row) => {
    const contract = row.contract;
    setEditingContractId(contract.id);
    setFormData({
      details: contract.details || '',
      upstream_contract: contract.upstream_contract || '',
      upstream_review: contract.upstream_review || ''
    });
  };
  const handleCancel = () => {
    setEditingContractId(null);
    setFormData({});
  };
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSave = async contract => {
    setSaving(true);
    await onUpdate({
      id: contract.id,
      data: formData
    });
    setEditingContractId(null);
    setFormData({});
    setSaving(false);
  };
  const handleDelete = async (index, row) => {
    if (window.confirm(t('confirm_delete'))) {
      await onDelete(row.contract.id);
    }
  };
  const canSave = formData.details?.trim() !== '' && formData.upstream_contract?.trim() !== '' && formData.upstream_review?.trim() !== '';
  const headers = useMemo(() => [{
    text: t('contract_details'),
    width: '30%'
  }, {
    text: t('upstream_contract'),
    width: '20%'
  }, {
    text: t('upstream_review'),
    width: '15%'
  }, {
    text: '',
    width: '10%'
  }], [t]);
  const rows = useMemo(() => {
    return contracts.map(contract => {
      const isEditing = editingContractId === contract.id;
      const isSavingThisRow = saving && isEditing;
      const columns = isEditing ? [/*#__PURE__*/React.createElement(Form.Control, {
        size: "sm",
        name: "details",
        value: formData.details,
        onChange: e => handleChange('details', e.target.value),
        placeholder: t('contract_details')
      }), /*#__PURE__*/React.createElement(Form.Select, {
        size: "sm",
        name: "upstream_contract",
        value: formData.upstream_contract,
        onChange: e => handleChange('upstream_contract', e.target.value)
      }, /*#__PURE__*/React.createElement("option", {
        value: ""
      }, t('select')), UPSTREAM_CONTRACT_OPTIONS.map(option => /*#__PURE__*/React.createElement("option", {
        key: option.value,
        value: option.value
      }, option.label))), /*#__PURE__*/React.createElement(Form.Select, {
        size: "sm",
        name: "upstream_review",
        value: formData.upstream_review,
        onChange: e => handleChange('upstream_review', e.target.value)
      }, /*#__PURE__*/React.createElement("option", {
        value: ""
      }, t('select')), UPSTREAM_REVIEW_OPTIONS.map(option => /*#__PURE__*/React.createElement("option", {
        key: option.value,
        value: option.value
      }, option.label))), /*#__PURE__*/React.createElement("div", {
        className: "d-flex justify-content-end gap-2"
      }, /*#__PURE__*/React.createElement(SaveButton, {
        size: "sm",
        onClick: () => handleSave(contract),
        disabled: isSavingThisRow || !canSave,
        text: isSavingThisRow ? t('saving') : t('save')
      }), /*#__PURE__*/React.createElement(CancelButton, {
        size: "sm",
        onClick: handleCancel,
        disabled: isSavingThisRow
      }))] : [/*#__PURE__*/React.createElement("small", null, contract.details), /*#__PURE__*/React.createElement(Badge, {
        bg: "primary"
      }, t(contract.upstream_contract)), /*#__PURE__*/React.createElement(Badge, {
        bg: "primary"
      }, t(contract.upstream_review)), userCanEdit ? /*#__PURE__*/React.createElement("div", {
        className: "d-flex justify-content-end gap-2"
      }, /*#__PURE__*/React.createElement(EditButton, {
        size: "sm",
        onClick: () => handleEdit(null, {
          contract
        })
      }), /*#__PURE__*/React.createElement(DeleteButton, {
        size: "sm",
        onClick: () => handleDelete(null, {
          contract
        })
      })) : null];
      return {
        key: `${contract.id}-${isEditing ? 'edit' : 'view'}`,
        contract: contract,
        columns: columns
      };
    });
  }, [contracts, editingContractId, formData, userCanEdit, saving, t]);
  return /*#__PURE__*/React.createElement("div", {
    className: "customer-contracts-table"
  }, /*#__PURE__*/React.createElement(Table, {
    headers: headers,
    rows: rows,
    showControls: false,
    opts: {
      bordered: false,
      striped: true,
      responsive: true,
      hover: true,
      headerSticky: true
    },
    style: {
      borderCollapse: 'collapse'
    }
  }));
};
export default CustomerContractsTable;